import { getBaseURL, getClient } from '../BLL/clients/HttpClient';
import { ApiEndPoint } from '../BLL/Enums/ApiEndPoint';

// Define the CMS-1500 service type
export interface Cms1500Service {
  id?: string;
  serviceFromDate: Date | null;
  serviceToDate: Date | null;
  cpt: string; // CPT/HCPCS code
  modifier: string; // Modifier code
  diagnosisPointer: string;
  totalCharge: number;
  serviceUnits: number;
  medicareRate?: number | null;
  multiplier?: number | null;
  isDirty?: boolean;
}

// Define the CMS-1500 form type
export interface Cms1500Form {
  id?: string;
  fileUrl?: string;
  policyNumber: string;
  patientName: string;
  providerName: string;
  providerAddress: string;
  providerCity: string;
  providerState: string;
  providerZip: string;
  totalSumCharge: number;
  services: Cms1500Service[];
  isDirty?: boolean;
  isCheckedManually: boolean;
  checkedBy?: string;
}

// Create an empty CMS-1500 service
export const createEmptyCMS1500Service = (): Cms1500Service => {
  return {
    serviceFromDate: null,
    serviceToDate: null,
    cpt: '',
    modifier: '',
    diagnosisPointer: '',
    totalCharge: 0,
    serviceUnits: 0,
    medicareRate: null,
    multiplier: null,
    isDirty: false,
  };
};

// Create an empty CMS-1500 form
export const createEmptyCMS1500Form = (policyNumber: string): Cms1500Form => {
  return {
    policyNumber,
    patientName: '',
    providerName: '',
    providerAddress: '',
    providerCity: '',
    providerState: '',
    providerZip: '',
    totalSumCharge: 0,
    isCheckedManually: false,
    checkedBy: '',
    services: [createEmptyCMS1500Service()],
    isDirty: false,
  };
};

// This function is now just a helper that returns an empty array
// The actual implementation is in the SearchCMS1500Claims component using the ClaimsContext
export const getCMS1500FormsByPolicyNumber = async (
  policyNumber: string,
  claimId?: string,
  formId?: string
): Promise<Cms1500Form[]> => {
  // If we have both claimId and formId, fetch the specific form
  if (claimId && formId) {
    try {
      const form = await getCMS1500FormById(claimId, formId);
      return [form];
    } catch (error) {
      console.error(`Error fetching CMS-1500 form ${formId}:`, error);
      return [];
    }
  }

  // Otherwise, return an empty array
  // The actual implementation is now in the SearchCMS1500Claims component
  return [];
};

// Get a specific CMS-1500 form by ID
export const getCMS1500FormById = async (
  claimId: string,
  formId: string
): Promise<Cms1500Form> => {
  try {
    const authBaseUrl = getBaseURL(ApiEndPoint.Auth);
    const client = getClient({ baseURL: authBaseUrl });
    // Use the specific endpoint for getting a CMS-1500 form
    const response = await client.get(`v1/claims/${claimId}/cms1500/${formId}`);
    const form = response.data;

    // Convert dates to Date objects
    const formResults =  {
      ...form,
      services: form.services.map((service: any) => ({
        ...service,
        serviceFromDate: service.serviceFromDate
          ? new Date(service.serviceFromDate)
          : '',
        serviceToDate: service.serviceToDate
          ? new Date(service.serviceToDate)
          : '',
      })),
    };

    // iterate through keys if they are null, set them to empty string
    Object.keys(formResults).forEach((key) => {
      if (formResults[key] === null) {
        formResults[key] = '';
      }
    });
    return formResults;
  } catch (error) {
    console.error(`Error fetching CMS-1500 form ${formId}:`, error);
    throw error;
  }
};

// Save CMS-1500 form
export const saveCMS1500Form = async (
  claimId: string,
  formId: string,
  form: Cms1500Form
): Promise<Cms1500Form> => {
  try {
    const formToSave = {
      ...form,
      services: form.services.map((service) => ({
        serviceFromDate: service.serviceFromDate,
        serviceToDate: service.serviceToDate,
        cpt: service.cpt,
        modifier: service.modifier,
        diagnosisPointer: service.diagnosisPointer,
        totalCharge: service.totalCharge,
        serviceUnits: service.serviceUnits,
      })),
    };
    delete formToSave.id;
    delete formToSave.isDirty;
    delete formToSave.fileUrl;
    const authBaseUrl = getBaseURL(ApiEndPoint.Auth);
    const client = getClient({ baseURL: authBaseUrl });
    const response = await client.put(
      `v1/claims/${claimId}/cms1500/${formId}`,
      formToSave
    );
    return response.data;
  } catch (error) {
    console.error('Error saving CMS-1500 form:', error);
    throw error;
  }
};

// Save CMS-1500 form
export const upsertCMS1500Forms = async (
  forms: Cms1500Form[]
): Promise<{
  created: number;
  updated: number;
  skipped: number;
}> => {
  try {
    const formsToSave = forms.map((form) => ({
      ...form,
      services: form.services.map((service) => ({
        serviceFromDate: service.serviceFromDate,
        serviceToDate: service.serviceToDate,
        cpt: service.cpt,
        modifier: service.modifier,
        diagnosisPointer: service.diagnosisPointer,
        totalCharge: service.totalCharge,
        serviceUnits: service.serviceUnits,
      })),
    }));

    for (const form of formsToSave) {
      if (!form.id) {
        delete form.id; // Remove ID to allow create
      }
      delete form.isDirty; // Remove dirty state
      delete form.fileUrl; // Remove file URL if present
    }

    const authBaseUrl = getBaseURL(ApiEndPoint.Auth);
    const client = getClient({ baseURL: authBaseUrl });
    const response = await client.put<{
      created: number;
      updated: number;
      skipped: number;
    }>(`v1/claims/cms1500`, formsToSave);
    console.log('Upsert CMS-1500 forms response:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error saving CMS-1500 form:', error);
    throw error;
  }
};

// Get Medicare rates for a CMS-1500 form
export const getMedicareRates = async (
  claimId: string,
  formId: string
): Promise<Cms1500Service[]> => {
  try {
    const authBaseUrl = getBaseURL(ApiEndPoint.Auth);
    const client = getClient({ baseURL: authBaseUrl });
    const response = await client.get(
      `v1/claims/${claimId}/cms1500/${formId}/medicare-rates`
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching Medicare rates:', error);
    throw error;
  }
};
